import { CalendarDays, type LucideIcon, ThumbsDown, ThumbsUp, User } from 'lucide-react'

import { cn } from '@fortem-monorepo/js-utilities'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Separator,
} from '@fortem-monorepo/react-ui'
import { PnlAttributions } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { PERMISSIONS } from '@/constants/permissions'
import { usePnlAttributionsActions } from '@/pages/operations/pnl-attributions/pnl-attribution-actions'
import { useInternalUser } from '@/react-query/queries/account'
import { formatNumber } from '@/utilities/format'

export interface InspectRequestDialogProps extends DialogProps {
  request: PnlAttributions.AttributionRequestRow
}

export const InspectRequestDialog: React.FC<InspectRequestDialogProps> = ({ request, ...props }) => {
  const { openApproveRequest, openRejectRequest } = usePnlAttributionsActions()
  const { internalUser } = useInternalUser()

  const isSameUser = internalUser?.user_id === request.requested_by

  return (
    <Dialog {...props}>
      <DialogContent className="max-h-[85vh] max-w-200! flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="shrink-0 border-b px-6 py-5">
          <div className="flex items-start justify-between gap-4 pr-6">
            <div className="min-w-0">
              <div className="mb-2 flex items-center gap-2">
                <Badge variant="outline">Request #{request.component_request_id}</Badge>
                <StatusBadge status={request.status} />
              </div>

              <DialogTitle className="text-xl">P&amp;L adjustment</DialogTitle>

              <p className="mt-1 text-sm text-muted-foreground">
                {request.unique_identifier}
                <span className="mx-2">·</span>
                Value date {formatDate(request.value_date)}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-8 p-6">
            <Section title="Request">
              <DetailsGrid>
                <Detail label="Type" value={request.request_type} />
                <Detail label="Component" value={request.component_type} />
                <Detail label="Target level" value={request.target_level} />
                <Detail label="Allocation rule" value={formatEnum(request.allocation_rule)} />
                <Detail label="Base net pnl" value={formatNumber(request.base_net_pnl) || '-'} />
                <Detail label="Target net pnl" value={formatNumber(request.target_net_pnl) || '-'} />
                <Detail label="Total Amount" value={formatNumber(request.total_amount_base)} />
              </DetailsGrid>
            </Section>

            <Section title="Instrument">
              <DetailsGrid>
                <Detail label="Unique identifier" value={request.unique_identifier} />
                <Detail label="Instrument ID" value={request.instrument_id} />
                <Detail label="Strategy ID" value={request.strategy_id} />
                <Detail label="Trade ID" value={request.trade_id} mono />
              </DetailsGrid>
            </Section>

            <Section title="Request history">
              <div className="flex flex-col gap-4">
                <div className="space-y-2">
                  <TimelineItem
                    icon={User}
                    title="Request submitted"
                    actor={request.requested_by_name ?? `User ${request.requested_by}`}
                    datetime={request.requested_datetime_utc}
                    active
                  />

                  {request.reason && (
                    <div className="ml-11 rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Reason</p>
                      <p className="mt-1 text-sm italic">"{request.reason}"</p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  {request.status !== 'PENDING' && (
                    <TimelineItem
                      icon={CalendarDays}
                      title={request.status === 'APPLIED' ? 'Adjustment applied' : 'REJECTED'}
                      actor={request.approved_by_name}
                      datetime={request.approved_datetime_utc}
                    />
                  )}

                  {request.decision_comment && (
                    <div className="ml-11 rounded-lg border bg-muted/30 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Decision comment</p>
                      <p className="mt-1 text-sm italic">"{request.decision_comment}"</p>
                    </div>
                  )}
                </div>
              </div>
            </Section>

            <Section title="Technical details">
              <DetailsGrid>
                <Detail label="Review batch ID" value={request.review_batch_id} />
                <Detail label="Base population hash" value={request.base_population_hash} mono />
              </DetailsGrid>
            </Section>
          </div>
        </div>

        {request.status === 'PENDING' && (
          <DialogFooter className={'w-full shrink-0 items-center justify-end border-t bg-background p-3'}>
            {isSameUser && (
              <span className={'text-muted-foreground text-sm'}>
                You cannot amend this request as you are the user who created it.
              </span>
            )}
            <Button size="sm" variant="destructive" onClick={() => props.onOpenChange?.(false)}>
              Cancel
            </Button>
            <CanAccess permissions={[PERMISSIONS.APPROVE_PNL_VALIDATION]}>
              <Button
                size="sm"
                variant="destructive"
                disabled={isSameUser}
                onClick={() => openRejectRequest({ component_request_id: request.component_request_id })}
              >
                Reject <ThumbsDown />
              </Button>
              <Button
                size="sm"
                variant="default"
                disabled={isSameUser}
                onClick={() => openApproveRequest({ component_request_id: request.component_request_id })}
              >
                Approve <ThumbsUp />
              </Button>
            </CanAccess>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-4 flex items-center gap-3">
        <h3 className="shrink-0 text-sm font-semibold">{title}</h3>
        <Separator className={'flex w-full shrink'} />
      </div>

      {children}
    </section>
  )
}

function DetailsGrid({ children }: { children: React.ReactNode }) {
  return <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2">{children}</dl>
}

function Detail({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>

      <dd className={mono ? 'mt-1 break-all font-mono text-xs' : 'mt-1 wrap-break-word text-sm font-medium'}>
        {displayValue(value)}
      </dd>
    </div>
  )
}

function TimelineItem({
  icon: Icon,
  title,
  actor,
  datetime,
  active = false,
  muted = false,
}: {
  icon: LucideIcon
  title: string
  actor?: string | null
  datetime?: string | null
  active?: boolean
  muted?: boolean
}) {
  return (
    <div className="grid grid-cols-[2rem_1fr] gap-x-3">
      <div className="flex flex-col items-center">
        <div
          className={[
            'flex size-8 shrink-0 items-center justify-center rounded-full border bg-background',
            active && 'border-primary bg-primary text-primary-foreground',
            muted && 'text-muted-foreground',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <Icon className="size-4" />
        </div>

        <div className="my-1 min-h-5 w-px flex-1 bg-border last:hidden" />
      </div>

      <div className="">
        <p className={muted ? 'text-sm text-muted-foreground' : 'text-sm font-medium'}>{title}</p>

        {(actor || datetime) && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {[actor, formatDateTime(datetime)].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
    </div>
  )
}

function displayValue(value: React.ReactNode) {
  return value === null || value === undefined || value === '' ? (
    <span className="font-normal text-muted-foreground">—</span>
  ) : (
    value
  )
}

function formatEnum(value: string | null | undefined) {
  if (!value) return null

  return value
    .toLowerCase()
    .split('_')
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ')
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—'

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
  }).format(new Date(value))
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return null

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

function StatusBadge({ status }: { status: string }) {
  const pending = status === 'PENDING'
  const applied = status === 'APPLIED'
  const rejected = status === 'REJECTED'

  return (
    <Badge
      className={cn({
        'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50': pending,
        'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50': applied,
        'border-red-200 bg-red-50 text-red-700 hover:bg-red-50': rejected,
      })}
      variant={pending ? 'outline' : 'secondary'}
    >
      {formatEnum(status)}
    </Badge>
  )
}
